import { useCallback, useEffect, useRef, useState } from 'react'
import { useThemeMode } from '@/contexts/ThemeContext'
import { 
  FONT_OPTIONS, 
  DEFAULT_FONT_ID, 
  getFontsByCategory, 
  CATEGORY_LABELS,
  type FontOption 
} from '@/config/fonts'
import { 
  loadAndApplyFont, 
  resetFontToDefault, 
  getSelectedFontId,
  loadGoogleFont 
} from '@/lib/fontLoader'
import { theme } from '@/lib/theme'

interface GoogleFontSelectorProps {
  isCompact?: boolean
}

export default function GoogleFontSelector({ isCompact = false }: GoogleFontSelectorProps) {
  const { colors } = useThemeMode()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedFontId, setSelectedFontId] = useState(DEFAULT_FONT_ID)
  const [loadingFontId, setLoadingFontId] = useState<string | null>(null)
  const [hoveredFont, setHoveredFont] = useState<FontOption | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  const fontsByCategory = getFontsByCategory()
  const selectedFont = FONT_OPTIONS.find(f => f.id === selectedFontId) || FONT_OPTIONS[0]
  const isDefault = selectedFontId === DEFAULT_FONT_ID

  // Load saved font on mount
  useEffect(() => {
    const saved = getSelectedFontId()
    setSelectedFontId(saved)
  }, [])

  // Listen for external font changes
  useEffect(() => {
    const handler = (e: CustomEvent) => {
      if (e.detail?.fontId) {
        setSelectedFontId(e.detail.fontId)
      }
    }
    window.addEventListener('kyogre-font-changed', handler as EventListener)
    return () => window.removeEventListener('kyogre-font-changed', handler as EventListener)
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return
    
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Preload font on hover for instant preview
  const handleFontHover = useCallback(async (font: FontOption) => {
    setHoveredFont(font)
    if (font.source === 'google') {
      try {
        await loadGoogleFont(font)
      } catch {
        // Ignore preload errors
      }
    }
  }, [])

  const handleFontSelect = useCallback(async (font: FontOption) => {
    setLoadingFontId(font.id)
    try {
      await loadAndApplyFont(font.id)
      setSelectedFontId(font.id)
      setIsOpen(false)
    } catch (error) {
      console.error('Failed to apply font:', error)
    } finally {
      setLoadingFontId(null)
    }
  }, [])

  const handleReset = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    resetFontToDefault()
    setSelectedFontId(DEFAULT_FONT_ID)
  }, [])

  return (
    <div 
      ref={dropdownRef}
      style={{ 
        position: 'relative', 
        display: 'flex', 
        alignItems: 'center', 
        gap: isCompact ? '4px' : '8px' 
      }}
    >
      {/* Label */}
      <span
        style={{
          fontSize: isCompact ? '10px' : '12px',
          fontWeight: 500,
          color: colors.secondaryText,
          whiteSpace: 'nowrap',
        }}
      >
        Font:
      </span>

      {/* Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: isCompact ? '4px 8px' : '6px 12px',
          backgroundColor: colors.primaryBg,
          border: `1px solid ${colors.primaryBorder}`,
          borderRadius: '4px',
          color: colors.primaryText,
          fontSize: isCompact ? '10px' : '12px',
          cursor: 'pointer',
          fontFamily: selectedFont.family,
          transition: 'all 0.15s ease',
          minWidth: isCompact ? '100px' : '140px',
          justifyContent: 'space-between',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = colors.primaryAccent
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = colors.primaryBorder
        }}
      >
        <span style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          flex: 1,
          textAlign: 'left',
        }}>
          {selectedFont.name}
        </span>
        <span style={{ 
          fontSize: '8px', 
          opacity: 0.6,
          flexShrink: 0,
        }}>
          {isOpen ? '▲' : '▼'}
        </span>
      </button>

      {/* Reset Button (only show if not default) */}
      {!isDefault && (
        <button
          onClick={handleReset}
          title="Reset to default font"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: isCompact ? '22px' : '26px',
            height: isCompact ? '22px' : '26px',
            padding: 0,
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: '4px',
            color: colors.tertiaryText,
            fontSize: isCompact ? '13px' : '15px',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.secondaryBg
            e.currentTarget.style.color = colors.primaryText
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
            e.currentTarget.style.color = colors.tertiaryText
          }}
        >
          ×
        </button>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            backgroundColor: colors.primaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: '6px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            minWidth: '220px',
            maxHeight: '360px',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {/* Category sections */}
          {Object.entries(fontsByCategory).map(([category, fonts]) => (
            <div key={category}>
              {/* Category Header */}
              <div
                style={{
                  padding: '8px 12px 4px',
                  fontSize: '9px',
                  fontWeight: 600,
                  color: colors.tertiaryText,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  borderTop: category !== Object.keys(fontsByCategory)[0] 
                    ? `1px solid ${colors.primaryBorder}` 
                    : 'none',
                  marginTop: category !== Object.keys(fontsByCategory)[0] ? '4px' : 0,
                  fontFamily: theme.fontFamily.mono,
                }}
              >
                {CATEGORY_LABELS[category] || category}
              </div>

              {/* Font Options */}
              {fonts.map((font) => {
                const isSelected = font.id === selectedFontId
                const isLoading = font.id === loadingFontId
                const isHovered = hoveredFont?.id === font.id

                return (
                  <button
                    key={font.id}
                    onClick={() => handleFontSelect(font)}
                    onMouseEnter={() => handleFontHover(font)}
                    onMouseLeave={() => setHoveredFont(null)}
                    disabled={isLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      width: '100%',
                      padding: '8px 12px',
                      backgroundColor: isSelected 
                        ? colors.folderSelectedBg 
                        : isHovered 
                          ? colors.secondaryBg 
                          : 'transparent',
                      border: 'none',
                      color: isSelected ? colors.folderSelectedText : colors.primaryText,
                      fontSize: '12px',
                      cursor: isLoading ? 'wait' : 'pointer',
                      textAlign: 'left',
                      fontFamily: font.family,
                      transition: 'background-color 0.1s ease',
                      opacity: isLoading ? 0.6 : 1,
                    }}
                  >
                    <span style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      flex: 1,
                    }}>
                      {isLoading ? 'Loading…' : font.name}
                    </span>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

